import { FILTER_USER_MAP } from './constants'
import { siteLicenseChecker } from 'lib/util'
import { selectSiteForUserManagement } from './selectors'

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

export const isLoggedInWithinLast7Days = user => {
  if (!user?.lastLoggedInAt) {
    return false
  }

  const lastLogin = new Date(user.lastLoggedInAt).getTime()
  return lastLogin >= Date.now() - SEVEN_DAYS_MS
}

export const FILTER_CONFIG = {
  all: {
    filter: users => users,
    query: '',
  },
  admin: {
    filter: users => users.filter(user => user.roles.includes('Admin')),
    query: '?role=Administrator',
  },
  teacher: {
    filter: users => users.filter(user => user.roles.includes('Teacher')),
    query: '?role=Teacher',
  },
  pending: {
    filter: users =>
      users.filter(user => user.isActive === true && user.status === 'Invited'),
    query: '?accountStatus=pending',
  },
  noClasses: {
    filter: users => users.filter(user => user.classesCreated === 0),
    query: '?classCount=0',
  },
  leaderTeam: {
    filter: users => users.filter(user => user.roles.includes('Leader Team')),
    query: '?role=LeaderTeam',
  },
  loggedInLast7Days: {
    filter: users =>
      users
        .filter(isLoggedInWithinLast7Days)
        .sort(
          (a, b) =>
            new Date(b.lastLoggedInAt).getTime() -
            new Date(a.lastLoggedInAt).getTime(),
        ),
    query: '?loggedIn=last7days',
  },
}

export const getFilterOptionFromQuery = query => {
  if (!query) {
    return { key: 'all', name: FILTER_USER_MAP.all }
  }

  if (query.accountStatus === 'pending') {
    return { key: 'pending', name: FILTER_USER_MAP.pending }
  }

  if (query.classCount === '0' || query.classCount === 0) {
    return { key: 'noClasses', name: FILTER_USER_MAP.noClasses }
  }

  if (query.loggedIn === 'last7days') {
    return {
      key: 'loggedInLast7Days',
      name: FILTER_USER_MAP.loggedInLast7Days,
    }
  }

  const roleMap = {
    Administrator: { key: 'admin', name: FILTER_USER_MAP.admin },
    LeaderTeam: { key: 'leaderTeam', name: FILTER_USER_MAP.leaderTeam },
    Teacher: { key: 'teacher', name: FILTER_USER_MAP.teacher },
  }

  if (query.role && roleMap[query.role]) {
    return roleMap[query.role]
  }

  return { key: 'all', name: FILTER_USER_MAP.all }
}

export const calculateUserCounts = activeUsers => {
  return activeUsers.reduce(
    (counts, user) => {
      const role = user.roles[0]
      const isActive = !!user.userId

      switch (role) {
        case 'Admin':
          counts.admin++
          isActive ? counts.adminActive++ : counts.adminPending++
          break
        case 'Leader Team':
          counts.leaderTeam++
          isActive ? counts.leaderTeamActive++ : counts.leaderTeamPending++
          break
        case 'Teacher':
          counts.teacher++
          isActive ? counts.teacherActive++ : counts.teacherPending++
          break
        default:
          console.warn(`Unknown role: ${role}`)
      }

      if (!isActive) {
        counts.pending++
      }

      if (user.classesCreated === 0) {
        counts.noClasses++
      }

      if (isLoggedInWithinLast7Days(user)) {
        counts.loggedInLast7Days++
      }

      return counts
    },
    {
      pending: 0,
      all: activeUsers.length,
      admin: 0,
      adminActive: 0,
      adminPending: 0,
      leaderTeam: 0,
      leaderTeamActive: 0,
      leaderTeamPending: 0,
      teacher: 0,
      teacherActive: 0,
      teacherPending: 0,
      noClasses: 0,
      loggedInLast7Days: 0,
    },
  )
}

// memos

export const memoSelectedUserManagementSite = (adminSites, siteId) =>
  selectSiteForUserManagement(adminSites, siteId)

export const memoSiteLicenseFlags = site =>
  site ? siteLicenseChecker(site) : {}

export const memoActiveSiteUsers = siteUsers =>
  siteUsers ? siteUsers.filter(user => user.isActive) : []

export const memoUserFilterOptionsAndCounts = (activeUsers, hasSelaLicense) => {
  if (!activeUsers.length) {
    return {
      userCounts: null,
      userFilterOptions: [],
      userInviteDialogCounts: null,
    }
  }

  const newUserCounts = calculateUserCounts(activeUsers)

  const filterUserCounts = {
    all: newUserCounts.all,
    admin: newUserCounts.admin,
    teacher: newUserCounts.teacher,
    leaderTeam: hasSelaLicense ? newUserCounts.leaderTeam : undefined,
    pending: newUserCounts.pending,
    noClasses: newUserCounts.noClasses,
    loggedInLast7Days: newUserCounts.loggedInLast7Days,
  }

  Object.keys(filterUserCounts).forEach(key => {
    if (filterUserCounts[key] === undefined) {
      delete filterUserCounts[key]
    }
  })

  const userFilterOptions = Object.entries(filterUserCounts).map(
    ([key, count]) => ({
      name: `${FILTER_USER_MAP[key]} (${count})`,
      key,
    }),
  )

  const userInviteDialogCounts = {
    pending: newUserCounts.pending,
    all: newUserCounts.all,
    admin: newUserCounts.admin,
    leaderTeam: newUserCounts.leaderTeam,
    teacher: newUserCounts.teacher,
  }

  return {
    userCounts: newUserCounts,
    userFilterOptions,
    userInviteDialogCounts,
  }
}

export const memoFilteredUsersBySelectedOption = (
  siteUsers,
  activeUsers,
  selectedFilterKey,
) => {
  if (!siteUsers || !selectedFilterKey) {
    return activeUsers
  }

  const config = FILTER_CONFIG[selectedFilterKey]
  return config ? config.filter(siteUsers) : activeUsers
}
